import { Subproject } from '@/stores/SubprojectStore';
import { Add, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { SubprojectBreakdown } from './SubprojectBreakdown';
import { LineItemState, OPERATION_CATEGORIES } from './types';

const FONT_HEADER = 'Montserrat, sans-serif';

// Column widths shared across header and rows
const COL_LABEL = '1fr';
const COL_AMOUNT = '160px';

const headerTypographySx = {
  fontFamily: FONT_HEADER,
  fontWeight: 700,
  color: 'text.primary',
  textTransform: 'uppercase',
} as const;

// Shared sx for amount TextFields on light-background rows
const amountFieldSx = {
  '& input': { textAlign: 'right', color: 'primary.main' },
  '& .MuiInputAdornment-root': { color: 'primary.main' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(14, 70, 63, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(14, 70, 63, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#0E463F', borderWidth: 2 },
  },
  '& legend': { display: 'none' },
  '& .MuiOutlinedInput-notchedOutline': { top: 0 },
} as const;

interface RowProps {
  label: React.ReactNode;
  ownerValue: string;
  contractorValue: string;
  onOwnerChange: (val: string) => void;
  onContractorChange: (val: string) => void;
  action?: React.ReactNode;
  bgcolor?: string;
}

function AmountRow({
  label,
  ownerValue,
  contractorValue,
  onOwnerChange,
  onContractorChange,
  action,
  bgcolor,
}: RowProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${COL_LABEL} ${COL_AMOUNT} ${COL_AMOUNT} 40px`,
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        pl: 6,
        pr: 1,
        borderBottom: '1px solid',
        borderColor: 'rgba(14, 70, 63, 0.2)',
        bgcolor: bgcolor ?? '#D5E4D5',
        color: 'primary.main',
      }}
    >
      <Box>{label}</Box>
      <NumericFormat
        customInput={TextField}
        thousandSeparator={true}
        decimalScale={2}
        fixedDecimalScale={true}
        size='small'
        value={ownerValue}
        onValueChange={(values: any) => onOwnerChange(values.value)}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          },
        }}
        sx={amountFieldSx}
      />
      <NumericFormat
        customInput={TextField}
        thousandSeparator={true}
        decimalScale={2}
        fixedDecimalScale={true}
        size='small'
        value={contractorValue}
        onValueChange={(values: any) => onContractorChange(values.value)}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          },
        }}
        sx={amountFieldSx}
      />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>{action}</Box>
    </Box>
  );
}

interface OperationCostTableProps {
  lineItems: LineItemState[];
  subprojects: Subproject[];
  onChange: (items: LineItemState[]) => void;
}

export function OperationCostTable({
  lineItems,
  subprojects,
  onChange,
}: OperationCostTableProps) {
  const fixedItems = OPERATION_CATEGORIES.map((cat) => {
    const existing = lineItems.find((li) => li.category === cat.category);
    return (
      existing ?? {
        clientId: crypto.randomUUID(),
        section: 'operation' as const,
        category: cat.category,
        label: '',
        ownerAmountFiscalyear: '',
        contractorAmountFiscalyear: '',
        ownerAmountTotal: '',
        contractorAmountTotal: '',
        sortOrder: cat.sortOrder,
        subprojectBreakdowns: [],
      }
    );
  });

  const customItems = lineItems.filter((li) => li.category === 'custom');

  function updateItem(clientId: string, patch: Partial<LineItemState>) {
    const allItems = [...fixedItems, ...customItems];
    onChange(
      allItems.map((item) =>
        item.clientId === clientId ? { ...item, ...patch } : item,
      ),
    );
  }

  function addCustomRow() {
    const nextSortOrder =
      Math.max(...lineItems.map((li) => li.sortOrder), 8) + 1;
    const newItem: LineItemState = {
      clientId: crypto.randomUUID(),
      section: 'operation',
      category: 'custom',
      label: '',
      ownerAmountFiscalyear: '',
      contractorAmountFiscalyear: '',
      ownerAmountTotal: '',
      contractorAmountTotal: '',
      sortOrder: nextSortOrder,
      subprojectBreakdowns: [],
    };
    onChange([...fixedItems, ...customItems, newItem]);
  }

  function removeCustomRow(clientId: string) {
    onChange([
      ...fixedItems,
      ...customItems.filter((li) => li.clientId !== clientId),
    ]);
  }

  return (
    <Box>
      {/* Header row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${COL_LABEL} ${COL_AMOUNT} ${COL_AMOUNT} 40px`,
          gap: 1,
          bgcolor: 'background.paper',
          pl: 6,
          pr: 1,
          py: 2,
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Typography variant='caption' sx={headerTypographySx}>
          Annual Operation Cost Estimation
        </Typography>
        <Typography
          variant='caption'
          sx={{ ...headerTypographySx, textAlign: 'center' }}
        >
          Owner
        </Typography>
        <Typography
          variant='caption'
          sx={{ ...headerTypographySx, textAlign: 'center' }}
        >
          Contractor
        </Typography>
        <Box />
      </Box>

      {/* Fixed rows */}
      {OPERATION_CATEGORIES.map((cat, index) => {
        const item = fixedItems.find((fi) => fi.category === cat.category)!;
        const rowBgColor = index % 2 === 0 ? '#D5E4D5' : '#CCDCCC';
        const displayLabel = cat.hasBreakdown
          ? `${cat.label} (Total)`
          : cat.label;
        const label = (
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='body2'>{displayLabel}</Typography>
          </Stack>
        );

        return (
          <Box key={cat.category}>
            <AmountRow
              label={label}
              ownerValue={item.ownerAmountFiscalyear}
              contractorValue={item.contractorAmountFiscalyear}
              onOwnerChange={(val) =>
                updateItem(item.clientId, { ownerAmountFiscalyear: val })
              }
              onContractorChange={(val) =>
                updateItem(item.clientId, { contractorAmountFiscalyear: val })
              }
              bgcolor={rowBgColor}
            />
            {cat.hasBreakdown && subprojects.length > 0 && (
              <SubprojectBreakdown
                ownerTotal={item.ownerAmountFiscalyear}
                contractorTotal={item.contractorAmountFiscalyear}
                subprojects={subprojects}
                breakdowns={item.subprojectBreakdowns}
                onChange={(newBreakdowns) =>
                  updateItem(item.clientId, {
                    subprojectBreakdowns: newBreakdowns,
                  })
                }
              />
            )}
          </Box>
        );
      })}

      {/* Custom rows */}
      {customItems.map((item, index) => (
        <AmountRow
          key={item.clientId}
          label={
            <TextField
              size='small'
              placeholder='Variable name'
              value={item.label}
              onChange={(e) =>
                updateItem(item.clientId, { label: e.target.value })
              }
              fullWidth
              sx={{
                '& input': { color: 'primary.main' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(14, 70, 63, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(14, 70, 63, 0.5)' },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0E463F',
                    borderWidth: 2,
                  },
                },
                '& legend': { display: 'none' },
                '& .MuiOutlinedInput-notchedOutline': { top: 0 },
              }}
            />
          }
          ownerValue={item.ownerAmountFiscalyear}
          contractorValue={item.contractorAmountFiscalyear}
          onOwnerChange={(val) =>
            updateItem(item.clientId, { ownerAmountFiscalyear: val })
          }
          onContractorChange={(val) =>
            updateItem(item.clientId, { contractorAmountFiscalyear: val })
          }
          action={
            <IconButton
              size='small'
              onClick={() => removeCustomRow(item.clientId)}
              aria-label='Remove row'
            >
              <Close fontSize='small' />
            </IconButton>
          }
          bgcolor={
            (OPERATION_CATEGORIES.length + index) % 2 === 0
              ? '#D5E4D5'
              : '#CCDCCC'
          }
        />
      ))}

      {/* Add Variable button */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
        <Button
          startIcon={<Add />}
          size='small'
          onClick={addCustomRow}
          variant='text'
          sx={{
            backgroundColor: 'background.paper',
            color: 'white',
            mb: '15px',
          }}
        >
          Add Variable
        </Button>
      </Box>
    </Box>
  );
}
